import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ArrowRight, Wallet, FileText, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const TONE = {
  "Completed": "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  "Cancelled": "text-red-300 border-red-500/30 bg-red-500/10",
  "Scheduled": "text-gold-soft border-[color:var(--border-strong)] bg-gold/10",
  "In Progress": "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
  "Requested": "text-amber-300 border-amber-500/30 bg-amber-500/10",
};
const tone = (s) => TONE[s] || "text-white/70 border-white/20 bg-white/5";

export default function AccountBookings() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("upcoming");
  useEffect(() => { api.get("/client/bookings").then(({ data }) => setData(data)).catch(() => setData({ jobs: [], requests: [] })); }, []);

  const all = useMemo(() => {
    if (!data) return [];
    const jobs = (data.jobs || []).map((j) => ({ ...j, _sort: j.date || "" }));
    const reqs = (data.requests || []).map((r) => ({ ...r, _sort: r.date || "" }));
    return [...jobs, ...reqs];
  }, [data]);

  const filtered = useMemo(() => {
    if (tab === "completed") return all.filter((x) => x.status === "Completed");
    if (tab === "cancelled") return all.filter((x) => x.status === "Cancelled");
    return all.filter((x) => !["Completed", "Cancelled"].includes(x.status));
  }, [all, tab]);

  return (
    <div className="space-y-6" data-testid="account-bookings">
      <div><p className="kicker mb-1">My Ascendia</p><h1 className="font-display text-2xl sm:text-3xl font-semibold text-white">My bookings</h1></div>
      <div className="flex gap-2">
        {[["upcoming", "Upcoming"], ["completed", "Completed"], ["cancelled", "Cancelled"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`bookings-tab-${k}`} className={`rounded-full px-4 py-1.5 text-sm border ${tab === k ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"}`}>{l}</button>
        ))}
      </div>
      {data === null ? <p className="text-sm text-muted-c">Loading…</p> : filtered.length === 0 ? (
        <div className="ascendia-card p-10 text-center">
          <CalendarClock className="h-8 w-8 text-gold-soft mx-auto" />
          <p className="mt-4 text-white font-medium">Nothing here yet</p>
          <p className="mt-1 text-sm text-muted-c">Your {tab} bookings will appear here.</p>
          {tab === "upcoming" && <Link to="/request-time" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft">Check availability <ArrowRight className="h-4 w-4" /></Link>}
        </div>
      ) : (
        <div className="space-y-3" data-testid="bookings-list">
          {filtered.map((b) => (
            <div key={b.id} className="ascendia-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3"><span className="font-mono text-xs text-gold-soft">{b.ref}</span><span className={`text-[11px] px-2 py-0.5 rounded-full border ${tone(b.status)}`}>{b.status}</span></div>
                  <p className="mt-2 text-white font-medium">{b.service || "Service"}</p>
                  <p className="text-xs text-muted-c mt-0.5">{[b.city, b.date, b.slot].filter(Boolean).join(" · ") || "Date to be confirmed"}</p>
                </div>
                {b.payment && (
                  <div className="text-right">
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-white/20 text-white/70"><Wallet className="h-3 w-3" /> {b.payment.state}</span>
                    {b.payment.total > 0 && <p className="mt-1 text-xs text-muted-c">{b.payment.currency}{b.payment.collected} / {b.payment.currency}{b.payment.total}{b.payment.balance > 0 ? ` · ${b.payment.currency}${b.payment.balance} due` : ""}</p>}
                  </div>
                )}
              </div>
              {b.report_ready && b.portal_token && (
                <a href={`/portal/${b.portal_token}`} target="_blank" rel="noopener noreferrer" data-testid={`booking-report-${b.id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm text-gold-soft hover:text-gold"><FileText className="h-4 w-4" /> View completion report <ExternalLink className="h-3 w-3" /></a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
